import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { MarkdownMessage } from "@/components/MarkdownMessage";
import {
  Send,
  Loader2,
  Sparkles,
  ChevronDown,
  Check,
  X,
  MessageSquare,
  Paperclip,
  RefreshCcw,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { normalizeTier } from "@shared/llmProviders";

type TierId = "thinking" | "small";
type TierOption = { id: TierId; label: string };

const TIER_OPTIONS: TierOption[] = [
  { id: "thinking", label: "Thinking" },
  { id: "small", label: "Small" },
];

const DEFAULT_MODEL_ID: TierId = "thinking";
const MODEL_STORAGE_KEY = "lrscribe.sidechat.tier.v2";
const LEGACY_MODEL_STORAGE_KEY = "lrscribe.sidechat.modelId";

function readSavedTier(): TierId | null {
  if (typeof window === "undefined") return null;
  // Prefer the current key, but fall back to the legacy key so existing users
  // keep their prior choice. Either value (a tier or a legacy full model ID) is
  // normalized to a tier: legacy Opus -> "thinking", else -> "small".
  const saved =
    window.localStorage.getItem(MODEL_STORAGE_KEY) ||
    window.localStorage.getItem(LEGACY_MODEL_STORAGE_KEY);
  if (!saved || !saved.trim()) return null;
  return normalizeTier(saved);
}

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
};

export function SideChat({
  sessionId,
  transcription,
  notes,
  generatedDocument,
  onClose,
}: {
  sessionId: string;
  transcription: string;
  notes: string;
  generatedDocument: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [streamingContent, setStreamingContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [includeTranscription, setIncludeTranscription] = useState(false);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [includeDocument, setIncludeDocument] = useState(false);

  // Tier selection: a saved value is an explicit per-user override. With no
  // saved value we follow the admin default tier (from /api/models/config)
  // once it loads, falling back to DEFAULT_MODEL_ID until then.
  const [modelId, setModelId] = useState<string>(() => readSavedTier() ?? DEFAULT_MODEL_ID);
  const [hasUserPreference, setHasUserPreference] = useState<boolean>(
    () => readSavedTier() !== null,
  );

  const selectTier = (tier: TierId) => {
    setModelId(tier);
    setHasUserPreference(true);
    try {
      window.localStorage.setItem(MODEL_STORAGE_KEY, tier);
    } catch {}
  };

  // Reset chat when switching sessions
  useEffect(() => {
    setMessages([]);
    setInput("");
    setStreamingContent("");
    setIsStreaming(false);
    setIncludeTranscription(false);
    setIncludeNotes(false);
    setIncludeDocument(false);
  }, [sessionId]);

  const availableContexts = {
    transcription: !!transcription.trim(),
    notes: !!notes.trim(),
    document: !!generatedDocument.trim(),
  };

  function buildContextBlock(): string {
    const parts: string[] = [];
    if (includeTranscription && availableContexts.transcription) {
      parts.push(`### TRANSCRIPTION\n${transcription.trim()}`);
    }
    if (includeNotes && availableContexts.notes) {
      parts.push(`### CLINICAL NOTES\n${notes.trim()}`);
    }
    if (includeDocument && availableContexts.document) {
      parts.push(`### GENERATED DOCUMENT\n${generatedDocument.trim()}`);
    }
    if (parts.length === 0) return "";
    return (
      `You are assisting with a medical recording session. Use the following context provided by the user when answering.\n\n` +
      parts.join("\n\n") +
      `\n\n---\n\n`
    );
  }

  const selectedContextCount =
    (includeTranscription && availableContexts.transcription ? 1 : 0) +
    (includeNotes && availableContexts.notes ? 1 : 0) +
    (includeDocument && availableContexts.document ? 1 : 0);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  const selectedTier = TIER_OPTIONS.find((m) => m.id === modelId) ?? TIER_OPTIONS[0];

  const { data: modelsConfig } = useQuery<{
    provider: string;
    defaultTier: string;
    tiers: { thinking: string; small: string };
  }>({
    queryKey: ["/api/models/config"],
    staleTime: 60_000,
  });

  // When the user has not explicitly picked a tier, follow the admin default
  // tier as soon as the config loads (and whenever it changes).
  useEffect(() => {
    if (hasUserPreference) return;
    const adminTier = modelsConfig?.defaultTier;
    if (!adminTier) return;
    setModelId(normalizeTier(adminTier));
  }, [modelsConfig?.defaultTier, hasUserPreference]);

  async function handleSend(overrideText?: string) {
    const text =
      typeof overrideText === "string" ? overrideText.trim() : input.trim();
    if (!text || isStreaming) return;

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
    };
    const priorForApi = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));
    setMessages((prev) => [...prev, userMsg]);
    if (typeof overrideText !== "string") {
      setInput("");
    }
    setIsStreaming(true);
    setStreamingContent("");

    // Only attach context on the first turn (when there's no prior history),
    // so later turns don't repeatedly resend large blocks.
    const contextBlock =
      priorForApi.length === 0 ? buildContextBlock() : "";
    const apiUserContent = contextBlock
      ? `${contextBlock}USER QUESTION:\n${text}`
      : text;

    try {
      const res = await fetch("/api/chat/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          messages: [
            ...priorForApi,
            { role: "user", content: apiUserContent },
          ],
          model: modelId,
        }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`Stream failed: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      let streamError: string | null = null;

      outer: while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const json = line.slice(5).trim();
          let evt: any;
          try {
            evt = JSON.parse(json);
          } catch {
            continue;
          }
          if (evt.error) {
            streamError = evt.error;
            break outer;
          }
          if (evt.content) {
            full += evt.content;
            setStreamingContent(full);
          }
        }
      }

      if (streamError) throw new Error(streamError);
      if (!full) throw new Error("Empty response from model");

      setMessages((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, role: "assistant", content: full },
      ]);
    } catch (err: any) {
      toast({
        title: "Chat error",
        description: err?.message || "Failed to get response",
        variant: "destructive",
      });
    } finally {
      setIsStreaming(false);
      setStreamingContent("");
    }
  }

  const hasHistory = messages.length > 0 || isStreaming;

  const anyAvailable =
    availableContexts.transcription ||
    availableContexts.notes ||
    availableContexts.document;

  const contextSelector = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-8"
          disabled={!anyAvailable}
          data-testid="button-side-chat-context"
        >
          <Paperclip className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">
            Context
            {selectedContextCount > 0 && (
              <span className="ml-1 rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-[10px]">
                {selectedContextCount}
              </span>
            )}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="text-xs font-semibold text-muted-foreground px-2 py-1">
          Include with your message
        </div>
        {[
          {
            key: "transcription" as const,
            label: "Transcription",
            checked: includeTranscription,
            setter: setIncludeTranscription,
            available: availableContexts.transcription,
          },
          {
            key: "notes" as const,
            label: "Notes",
            checked: includeNotes,
            setter: setIncludeNotes,
            available: availableContexts.notes,
          },
          {
            key: "document" as const,
            label: "Generated document",
            checked: includeDocument,
            setter: setIncludeDocument,
            available: availableContexts.document,
          },
        ].map((opt) => (
          <label
            key={opt.key}
            className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm ${
              opt.available
                ? "cursor-pointer hover:bg-muted"
                : "opacity-50 cursor-not-allowed"
            }`}
            data-testid={`label-context-${opt.key}`}
          >
            <Checkbox
              checked={opt.checked && opt.available}
              disabled={!opt.available}
              onCheckedChange={(v) => opt.setter(!!v)}
              data-testid={`checkbox-context-${opt.key}`}
            />
            <span className="flex-1">{opt.label}</span>
            {!opt.available && (
              <span className="text-[10px] text-muted-foreground">
                unavailable
              </span>
            )}
          </label>
        ))}
        {messages.length > 0 && selectedContextCount > 0 && (
          <div className="text-[11px] text-muted-foreground px-2 pt-1 pb-1">
            Context is sent only on the first message of a chat.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );

  const tierSublabel = (tier: TierId): string | undefined => {
    if (!modelsConfig) return undefined;
    return tier === "thinking" ? modelsConfig.tiers.thinking : modelsConfig.tiers.small;
  };

  const modelSelector = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-8"
          data-testid="button-side-chat-model-selector"
        >
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-medium">{selectedTier.label}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {TIER_OPTIONS.map((tier) => {
          const isSelected = tier.id === modelId;
          const sublabel = tierSublabel(tier.id);
          return (
            <DropdownMenuItem
              key={tier.id}
              onClick={() => selectTier(tier.id)}
              data-testid={`menu-side-chat-model-${tier.id}`}
            >
              <div className="flex items-center justify-between w-full gap-2">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{tier.label}</span>
                  {sublabel && (
                    <span className="text-xs text-muted-foreground truncate max-w-[160px]">
                      {sublabel}
                    </span>
                  )}
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 text-primary shrink-0" />
                )}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <aside
      className="hidden lg:flex w-96 border-l border-border bg-card flex-col shrink-0"
      data-testid="pane-side-chat"
    >
      {/* Header */}
      <div className="h-12 border-b border-border flex items-center justify-between px-3 shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold">Side Chat</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onClose}
          data-testid="button-close-side-chat"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {!hasHistory ? (
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <div className="w-full text-center">
            <h3 className="text-base font-semibold mb-1">Ask about this session</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Start a conversation with the AI.
            </p>
            <div className="flex justify-center gap-2 mb-3 flex-wrap">
              {modelSelector}
              {contextSelector}
            </div>
            <div className="relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask anything..."
                rows={3}
                className="w-full p-3 pr-12 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                data-testid="input-side-chat-initial"
              />
              <Button
                size="icon"
                className="absolute right-2 bottom-2 h-8 w-8"
                onClick={() => handleSend()}
                disabled={!input.trim() || isStreaming}
                data-testid="button-side-chat-send-initial"
              >
                {isStreaming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <ScrollArea className="flex-1">
            <div ref={scrollRef} className="p-3 space-y-3">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex items-end gap-1 ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                  data-testid={`side-chat-message-${m.role}-${m.id}`}
                >
                  {m.role === "user" && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-foreground"
                            onClick={() => handleSend(m.content)}
                            disabled={isStreaming}
                            aria-label="Resend message"
                            data-testid={`button-resend-message-${m.id}`}
                          >
                            <RefreshCcw className="w-3.5 h-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="left">Resend message</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground whitespace-pre-wrap"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    {m.role === "user" ? (
                      m.content
                    ) : (
                      <MarkdownMessage content={m.content} />
                    )}
                  </div>
                </div>
              ))}
              {isStreaming && (
                <div
                  className="flex justify-start"
                  data-testid="side-chat-message-streaming"
                >
                  <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-muted text-foreground">
                    {streamingContent ? (
                      <MarkdownMessage content={streamingContent} />
                    ) : (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    )}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="border-t border-border p-3 space-y-2">
            <div className="flex flex-wrap gap-2">
              {modelSelector}
              {contextSelector}
            </div>
            <div className="relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Send a message..."
                rows={2}
                className="w-full p-2.5 pr-12 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                data-testid="input-side-chat-message"
              />
              <Button
                size="icon"
                className="absolute right-2 bottom-2 h-8 w-8"
                onClick={() => handleSend()}
                disabled={!input.trim() || isStreaming}
                data-testid="button-side-chat-send"
              >
                {isStreaming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </aside>
  );
}
